import api from './api';
import { User, Token } from '../../store/authStore';

// Extraction is a synchronous OCR → Mistral → sanctions → ATLAS round-trip.
// Timeout chain, ordered innermost → outermost (each layer needs headroom over the one inside it):
//   Mistral client read timeout    180s  (backend/app/core/mistral_client.py)
//   this axios upload timeout       240s  ← OCR + sanctions + serialization headroom over Mistral
//   nginx /api/ proxy_read_timeout  300s  (nginx/conf.d/default.conf)
// 240s < 300s, so the browser aborts with a clean ECONNABORTED before nginx returns a 504.
const EXTRACTION_UPLOAD_TIMEOUT_MS = 240_000;

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  phonenumber?: string;
  password: string;
  selected_modules?: string[];
}

export interface AuthResponse {
  user: User;
  token: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
  message?: string;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/api/v1/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post('/api/v1/auth/register', userData);
    return response.data;
  }

  async logout(): Promise<void> {
    await api.post('/api/v1/auth/logout');
  }

  async refreshToken(refreshToken: string): Promise<Token> {
    const response = await api.post('/api/v1/auth/refresh', {
      refresh_token: refreshToken
    });
    return response.data;
  }

  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    const response = await api.get('/api/v1/auth/verify');
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/v1/auth/me');
    return response.data;
  }

  async updateProfile(profileData: { firstname?: string; lastname?: string; phonenumber?: string; pinned_nav_path?: string | null }): Promise<User> {
    // Treat null on the wire as the explicit "clear" signal (empty string),
    // since PATCH/PUT semantics here use empty string to clear and undefined
    // to skip a field.
    const payload: Record<string, unknown> = { ...profileData };
    if (payload.pinned_nav_path === null) payload.pinned_nav_path = '';
    const response = await api.put('/api/v1/auth/me', payload);
    return response.data;
  }

  // Convenience wrapper used by the sidebar context menu. Pass `null` to
  // clear the pin, or a route string to set it.
  async setPinnedNavPath(path: string | null): Promise<User> {
    return this.updateProfile({ pinned_nav_path: path });
  }

  async updateLanguagePreference(language: string): Promise<{ success: boolean; message?: string; language?: string }> {
    try {
      console.log('Updating language preference to:', language);

      // Try the /me/language endpoint first (more standard)
      const response = await api.put('/api/v1/auth/me/language', { language });

      console.log('Language API response:', response.data);

      return {
        success: true,
        message: response.data.message,
        language: response.data.preferred_language || response.data.language || language,
        ...response.data
      };
    } catch (error: any) {
      console.error('Failed to update language preference:', error);
      console.error('Error response:', error.response?.data);

      // Try alternative endpoint if first one fails
      if (error.response?.status === 404) {
        try {
          console.log('Trying alternative endpoint /auth/language');
          const response = await api.put('/api/v1/auth/language', { language });
          return {
            success: true,
            message: response.data.message,
            language: response.data.preferred_language || response.data.language || language,
            ...response.data
          };
        } catch (altError: any) {
          console.error('Alternative endpoint also failed:', altError);
        }
      }

      return {
        success: false,
        message: error.response?.data?.detail || error.message || 'Failed to update language preference'
      };
    }
  }

  async updateThemePreference(preset: string | null, isDarkMode: boolean | null): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.put('/api/v1/auth/me/theme', { preset, isDarkMode });
      return {
        success: true,
        message: response.data.message,
        ...response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.detail || error.message || 'Failed to update theme preference'
      };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/api/v1/auth/request-password-reset', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/api/v1/auth/reset-password', {
      token,
      new_password: newPassword
    });
  }

  // Admin authentication methods
  async adminLogin(username: string, password: string): Promise<AuthResponse> {
    const response = await api.post('/api/v1/auth/login', {
      username,
      password
    });
    return response.data;
  }

  // File upload and extraction
  async uploadAndExtractData(formData: FormData) {
    try {
      // Use the files/upload endpoint that works in /process page
      const response = await api.post('/api/v1/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('File upload and extraction failed:', error);
      throw error;
    }
  }

  // Module management methods
  async getModules(): Promise<any[]> {
    try {
      // Use regular modules endpoint for users, admin endpoint for admins
      const response = await api.get('/api/v1/modules');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      return [];
    }
  }

  async getAvailableModules(): Promise<{ modules: any[]; total: number } | { message: string } | any[]> {
    try {
      // No authentication required for this endpoint
      const response = await api.get('/api/v1/auth/modules/available');

      // Handle placeholder response
      if (response.data && typeof response.data === 'object' && response.data.message) {
        console.log('Available modules API not implemented:', response.data.message);
        return { modules: [], total: 0 };
      }

      // Handle different response structures
      if (Array.isArray(response.data)) {
        return { modules: response.data, total: response.data.length };
      } else if (response.data && Array.isArray(response.data.modules)) {
        return { modules: response.data.modules, total: response.data.total || response.data.modules.length };
      }

      return response.data || { modules: [], total: 0 };
    } catch (error) {
      console.error('Failed to fetch available modules:', error);
      return { modules: [], total: 0 };
    }
  }

  async getAdminModules(): Promise<any[]> {
    try {
      const response = await api.get('/api/v1/admin/modules');
      // Handle placeholder response
      if (response.data && typeof response.data === 'object' && response.data.message) {
        console.log('Admin modules API not implemented:', response.data.message);
        return [];
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to fetch admin modules:', error);
      return [];
    }
  }

  async createModule(moduleData: any): Promise<any> {
    const response = await api.post('/api/v1/admin/modules', moduleData);
    return response.data;
  }

  async updateModule(moduleId: string, moduleData: any): Promise<any> {
    const response = await api.put(`/api/v1/admin/modules/${moduleId}`, moduleData);
    return response.data;
  }

  async deleteModule(moduleId: string): Promise<any> {
    const response = await api.delete(`/api/v1/admin/modules/${moduleId}`);
    return response.data;
  }

  // Admin user management methods
  async createUser(userData: any): Promise<any> {
    const response = await api.post('/api/v1/admin/users', userData);
    return response.data;
  }

  async updateUser(userId: string, userData: any): Promise<any> {
    const response = await api.put(`/api/v1/admin/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: string): Promise<any> {
    const response = await api.delete(`/api/v1/admin/users/${userId}`);
    return response.data;
  }

  async toggleUserActiveStatus(userId: string): Promise<any> {
    const response = await api.post(`/api/v1/admin/users/${userId}/toggle-active`);
    return response.data;
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<any> {
    const response = await api.post(`/api/v1/admin/users/${userId}/reset-password`, {
      new_password: newPassword
    });
    return response.data;
  }

  async resetUserAuthentication(userId: string): Promise<any> {
    const response = await api.post(`/api/v1/admin/users/${userId}/reset-authentication`);
    return response.data;
  }

  async getAllUsers(includeStats: boolean = false): Promise<any[]> {
    try {
      const response = await api.get('/api/v1/admin/users', {
        params: includeStats ? { include_stats: true } : undefined,
      });
      // Handle placeholder response
      if (response.data && typeof response.data === 'object' && response.data.message) {
        console.log('Users API not implemented:', response.data.message);
        return [];
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return [];
    }
  }

  // Group management methods
  async getGroups(): Promise<any[]> {
    try {
      const response = await api.get('/api/v1/admin/groups');
      // Handle placeholder response
      if (response.data && typeof response.data === 'object' && response.data.message) {
        console.log('Groups API not implemented:', response.data.message);
        return [];
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      return [];
    }
  }

  async createGroup(groupData: any): Promise<any> {
    const response = await api.post('/api/v1/admin/groups', groupData);
    return response.data;
  }

  async updateGroup(groupId: string, groupData: any): Promise<any> {
    const response = await api.put(`/api/v1/admin/groups/${groupId}`, groupData);
    return response.data;
  }

  async deleteGroup(groupId: string): Promise<any> {
    const response = await api.delete(`/api/v1/admin/groups/${groupId}`);
    return response.data;
  }

  async assignUserToGroup(userId: string, groupId: string): Promise<void> {
    await api.post(`/api/v1/admin/groups/${groupId}/users`, { user_id: userId });
  }

  async removeUserFromGroup(userId: string, groupId: string): Promise<void> {
    await api.delete(`/api/v1/admin/groups/${groupId}/users/${userId}`);
  }

  async setGroupModulePermissions(groupId: string, moduleIds: string[]): Promise<void> {
    try {
      await api.post(`/api/v1/admin/groups/${groupId}/modules`, { module_ids: moduleIds });
    } catch (error: any) {
      // If endpoint doesn't exist (404), throw a more descriptive error
      if (error.response?.status === 404) {
        throw new Error('Module permissions API not yet implemented');
      }
      throw error;
    }
  }

  // Audit Logs
  async getAuditLogs(params?: any): Promise<any> {
    const response = await api.get('/api/v1/admin/audit-logs', { params });
    return response.data;
  }

  async getAuditStats(days: number = 30): Promise<any> {
    const response = await api.get(`/api/v1/admin/audit-logs/stats`, { params: { days } });
    return response.data;
  }

  async getAuditEventTypes(): Promise<any> {
    const response = await api.get('/api/v1/admin/audit-logs/event-types');
    return response.data;
  }

  // File Processing
  async uploadFile(formData: FormData, signal?: AbortSignal): Promise<any> {
    const startTime = Date.now();

    try {
      console.log('🔄 Starting base64 file upload...', formData);

      // Extract file and module_id from FormData
      const file = formData.get('file') as File;
      const moduleId = formData.get('module_id') as string;

      if (!file || !moduleId) {
        throw new Error('Missing file or module_id in FormData');
      }

      console.log(`📁 Converting file to base64: ${file.name} (${file.size} bytes)`);

      // Convert file to base64
      const base64Content = await this.fileToBase64(file);

      // Create JSON payload
      const uploadData = {
        filename: file.name,
        content: base64Content,
        content_type: file.type,
        module_id: moduleId
      };

      console.log(`📡 Making POST request to /api/v1/files/upload with base64 data`);
      console.log(`📊 Payload size: ${JSON.stringify(uploadData).length} characters`);

      const response = await api.post('/api/v1/files/upload', uploadData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: EXTRACTION_UPLOAD_TIMEOUT_MS,
        signal, // Pass abort signal for cancellation
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`📊 Upload progress: ${percentCompleted}% (${progressEvent.loaded}/${progressEvent.total} bytes)`);
          }
        },
      });

      const endTime = Date.now();
      console.log(`✅ File upload successful in ${endTime - startTime}ms:`, response.data);
      
      return response.data;
    } catch (error: any) {
      const endTime = Date.now();
      console.error(`❌ File upload failed after ${endTime - startTime}ms:`, error);

      if (error.code === 'ECONNABORTED') {
        console.error('🕐 Upload timed out - request took too long');
      } else if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network error - could not connect to server');
      }

      console.error('Error details:', {
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      console.error('Full error response data:', JSON.stringify(error.response?.data, null, 2));
      throw error;
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/jpeg;base64, prefix
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  async extractFileData(fileId: string): Promise<any> {
    const response = await api.post(`/api/v1/files/${fileId}/extract`);
    return response.data;
  }

  // Persist reviewed/edited extracted data back onto a transaction so the
  // user's field corrections (incl. Teach-AI delete/re-map) survive a reload.
  // Used by the document-processing form's debounced autosave; failures are
  // swallowed by the caller (background save), so no toast is shown here.
  async updateTransactionData(
    transactionId: string,
    extractedData: any,
    signal?: AbortSignal
  ): Promise<any> {
    const response = await api.patch(
      `/api/v1/transactions/${transactionId}`,
      { extracted_data: extractedData },
      { signal }
    );
    return response.data;
  }

  // Module Authentication & Processing
  async checkModuleAuthStatus(moduleId: string): Promise<any> {
    const response = await api.get(`/api/v1/oauth/${moduleId}/status`);
    return response.data;
  }

  async authenticateModule(moduleId: string, credentials: { username: string; password: string }): Promise<any> {
    const response = await api.post(`/api/v1/oauth/${moduleId}/authenticate`, credentials);
    return response.data;
  }

  async revokeModuleAuth(moduleId: string): Promise<any> {
    const response = await api.post(`/api/v1/oauth/${moduleId}/revoke`);
    return response.data;
  }

  async validateModuleData(moduleId: string, data: any, language: string = 'de', sendPartialData: boolean = true): Promise<{
    valid: boolean;
    errors: any[];
    warnings: any[];
    total_errors?: number;
    total_warnings?: number;
    processing_time_ms?: number;
    validation_service?: string;
    service_endpoint?: string;
    validation_phases?: Record<string, {
      passed: boolean;
      errors: number;
      warnings?: number;
      description?: string;
      service_endpoint?: string;
      error_message?: string;
    }>;
    missing_documents?: string[];
    message?: string;
  }> {
    try {
      const response = await api.post(`/api/v1/modules/${moduleId}/validate`, {
        data,
        language,  // Pass language for error message translation
        send_partial_data: sendPartialData  // Pass send_partial_data for autosend configuration
      });
      return response.data;
    } catch (error: any) {
      // If validation endpoint errors, treat as invalid
      return {
        valid: false,
        errors: [{
          field: '_validation_service',
          message: error.response?.data?.detail || 'Validation service error',
          severity: 'error'
        }],
        warnings: [],
        total_errors: 1,
        total_warnings: 0
      };
    }
  }

  async sendToModule(moduleId: string, data: any, sendPartialData: boolean = true): Promise<any> {
    try {
      const response = await api.post(`/api/v1/modules/${moduleId}/push`, {
        data,
        send_partial_data: sendPartialData
      });
      return response.data;
    } catch (error: any) {
      // Handle authentication errors with structured error details
      if (error.response?.status === 401) {
        const errorDetail = error.response?.data?.detail;
        if (typeof errorDetail === 'object' && errorDetail.error) {
          // Structured error response
          const authError = new Error(errorDetail.message || 'Authentication error');
          Object.assign(authError, {
            authError: true,
            errorType: errorDetail.error,
            moduleName: errorDetail.module_name,
            authEndpoint: errorDetail.auth_endpoint,
            expiredAt: errorDetail.expired_at,
            originalError: error
          });
          throw authError;
        }
      }
      throw error;
    }
  }

  async getExportStatus(
    moduleId: string,
    startLimit: number = 0,
    endLimit: number = 50,
    filters?: { filterUser?: string; filterMrnlrn?: string; filterDatum?: string; filterStatus?: string }
  ): Promise<any> {
    const response = await api.get(`/api/v1/modules/${moduleId}/export-status`, {
      params: {
        start_limit: startLimit,
        end_limit: endLimit,
        filter_user: filters?.filterUser || 'current',
        filter_mrnlrn: filters?.filterMrnlrn || '',
        filter_datum: filters?.filterDatum || '',
        filter_status: filters?.filterStatus || '',
      },
    });
    return response.data;
  }

  async refreshCodelists(): Promise<any> {
    const response = await api.post('/api/v1/codelists/refresh');
    return response.data;
  }

  async getCodelistLastRefreshed(): Promise<any> {
    const response = await api.get('/api/v1/codelists/last-refreshed');
    return response.data;
  }


  async getTransactionHistory(params?: any): Promise<any> {
    const response = await api.get('/api/v1/transactions', { params });
    return response.data;
  }

  async getMyTransactions(params?: any): Promise<any> {
    const response = await api.get('/api/v1/transactions/my', { params });
    return response.data;
  }

  async getAllTransactions(params?: any): Promise<any> {
    const response = await api.get('/api/v1/admin/transactions', { params });
    return response.data;
  }

  async getTamperingHistory(params?: { limit?: number; offset?: number }): Promise<any> {
    const response = await api.get('/api/v1/tampering-detection/history', { params });
    return response.data;
  }

  async getTamperingHistoryByUserId(userId: string, params?: { limit?: number; offset?: number }): Promise<any> {
    const response = await api.get(`/api/v1/tampering-detection/history/${userId}`, { params });
    return response.data;
  }

  async getDashboardStats(): Promise<any> {
    const response = await api.get('/api/v1/auth/dashboard-stats');
    return response.data;
  }

  // Activity Statistics Methods
  async getActivityStats(): Promise<{
    total_documents: number;
    document_processing: number;
    tariff_classification: number;
    email_processing: number;
  }> {
    const response = await api.get('/api/v1/auth/me/activity-stats');
    return response.data;
  }

  async getUserActivityStats(userId: string): Promise<{
    total_documents: number;
    document_processing: number;
    tariff_classification: number;
    email_processing: number;
  }> {
    const response = await api.get(`/api/v1/admin/users/${userId}/activity-stats`);
    return response.data;
  }

  // User-Module Assignment Methods
  async getUserModules(userId: string): Promise<any[]> {
    try {
      const response = await api.get(`/api/v1/admin/users/${userId}/modules`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Failed to fetch user modules:', error);
      return [];
    }
  }

  async assignModulesToUser(userId: string, moduleIds: string[]): Promise<any> {
    const response = await api.post(`/api/v1/admin/users/${userId}/modules`, {
      module_ids: moduleIds
    });
    return response.data;
  }

  async removeModuleFromUser(userId: string, moduleId: string): Promise<void> {
    await api.delete(`/api/v1/admin/users/${userId}/modules/${moduleId}`);
  }

  // User Transaction Methods
  async getUserTransactions(userId: string, params?: any): Promise<any> {
    try {
      const response = await api.get(`/api/v1/admin/users/${userId}/transactions`, { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user transactions:', error);
      // Return mock data for now since the API endpoint doesn't exist yet
      throw error;
    }
  }

  async getTransactionDetails(transactionId: string): Promise<any> {
    try {
      const response = await api.get(`/api/v1/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
      throw error;
    }
  }

  async getTransactionDetailsAdmin(transactionId: string): Promise<any> {
    try {
      const response = await api.get(`/api/v1/admin/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch transaction details (admin):', error);
      throw error;
    }
  }

  // File management methods
  async getUserFiles(params?: any): Promise<any> {
    try {
      const response = await api.get('/api/v1/files', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user files:', error);
      throw error;
    }
  }

  async getFileInfo(fileId: string): Promise<any> {
    try {
      const response = await api.get(`/api/v1/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch file info:', error);
      throw error;
    }
  }

  async getFileViewUrl(fileId: string): Promise<any> {
    try {
      const response = await api.get(`/api/v1/files/${fileId}/view`);
      return response.data;
    } catch (error) {
      console.error('Failed to get file view URL:', error);
      throw error;
    }
  }

  // Admin file management methods
  async getAdminFiles(params?: any): Promise<any> {
    try {
      const response = await api.get('/api/v1/admin/files', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch admin files:', error);
      throw error;
    }
  }

  async getAdminTransactions(params?: any): Promise<any> {
    try {
      const response = await api.get('/api/v1/admin/transactions', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch admin transactions:', error);
      throw error;
    }
  }

  async getFileStatistics(): Promise<any> {
    try {
      const response = await api.get('/api/v1/admin/files/statistics');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch file statistics:', error);
      throw error;
    }
  }

  async updateUserLanguage(language: string): Promise<any> {
    try {
      const response = await api.put('/api/v1/auth/me/language', { language });
      return response.data;
    } catch (error) {
      console.error('Failed to update user language:', error);
      throw error;
    }
  }

  // User limit management methods
  async updateUserLimits(userId: string, limits: {
    monthly_document_limit?: number | null;
  }): Promise<any> {
    const response = await api.put(`/api/v1/admin/users/${userId}/limits`, limits);
    return response.data;
  }

  async resetUserUsage(userId: string): Promise<any> {
    const response = await api.post(`/api/v1/admin/users/${userId}/reset-usage`);
    return response.data;
  }

  async updateUserResetDate(userId: string, dateData: {
    monthly_reset_date: string;
  }): Promise<any> {
    const response = await api.put(`/api/v1/admin/users/${userId}/reset-date`, dateData);
    return response.data;
  }

  /**
   * Toggle individual ``has_*`` feature flags on a user account (admin only).
   *
   * Calls ``PUT /api/v1/admin/users/{userId}/features``.
   * The backend validates that every key is a real ``has_*`` column and that
   * every value is a boolean before writing anything to the DB.
   *
   * @param userId   UUID of the user to update.
   * @param features Partial map of boolean feature flags to set, e.g.
   *                 ``{ has_tariff_feedback: true }``.
   */
  async updateUserFeatures(userId: string, features: Record<string, boolean>): Promise<any> {
    const response = await api.put(`/api/v1/admin/users/${userId}/features`, features);
    return response.data;
  }

  // ATLAS Configuration Methods
  async getAtlasConfig(): Promise<any> {
    try {
      const response = await api.get('/api/v1/auth/me/atlas-config');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch ATLAS config:', error);
      throw error;
    }
  }

  async updateAtlasConfig(configData: {
    atlas_user_id?: string;
    atlas_eori_number?: string;
    atlas_customs_office?: string;
    atlas_gestellungszollstelle?: string;
    atlas_ausfuhrzollstelle?: string;
    company_name?: string;
    company_street?: string;
    company_postal_code?: string;
    company_city?: string;
    company_country?: string;
    default_currency?: string;
    default_incoterm?: string;
    default_country_origin?: string;
    default_transport_mode?: string;
  }): Promise<any> {
    try {
      const response = await api.patch('/api/v1/auth/me/atlas-config', configData);
      return response.data;
    } catch (error) {
      console.error('Failed to update ATLAS config:', error);
      throw error;
    }
  }

  // Module User Details Methods
  async getModuleUserDetails(moduleId: string): Promise<any> {
    try {
      const response = await api.get(`/api/v1/modules/${moduleId}/user-details`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch module user details:', error);
      return null;
    }
  }

}

const authService = new AuthService();
export default authService;